<template>

  <t-page>

    <b-modal
        id="modal-stock-detail-find-tolerances"
        scrollable
        no-close-on-backdrop
        no-close-on-esc
        @hidden="onCancelFindTolerances"
        title="Filtrar por Restrições de Cliente">

      <t-input-person customer required
                      v-model="form.customerId" @change="onChangeCustomerId"/>

      <t-msg info v-if="!loadCustomerRules">Escolha um cliente para verificar se há tolerâncias ou restrições de
        embalagens
      </t-msg>

      <div v-else>

        <t-msg info v-if="customerRules.enableAllUnits && customerRules.tolerances.length === 0">Este cliente não
          possui restrições de <b>embalagens</b> ou <b>tolerâncias específicas</b>
        </t-msg>

        <t-msg warning v-else>
          Este cliente possui restrições:
          <ul class="mb-0">
            <li v-if="!customerRules.enableAllUnits"><b>Unidades aceitas:</b> {{ customerRulesUnits }}</li>
            <li v-if="customerRules.tolerances.length > 0"><b>Tolerâncias restritas:</b>
              {{ customerRulesTolerances }}
            </li>
          </ul>
        </t-msg>

      </div>

      <template #modal-footer="{ cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancelar
        </b-button>

        <b-button variant="primary"
                  :disabled="!loadCustomerRules || (!customerRules.enableAllUnits && customerRules.tolerances.length === 0)"
                  @click="filtrarTolerancias">
          Filtrar Tolerâncias
        </b-button>
      </template>

    </b-modal>

    <t-page-general :current-page="currentPage">


      <template #title-buttons>

        <b-link class="btn btn-outline-secondary mr-4"
                href="https://custom.onlaine.com.br/dashboard/custom/polimix/dashboard_polimix_orientacoes.html"
                target="_blank">
          <b-icon-info-square class="mr-2"/>
          Orientações
        </b-link>

        <t-button @click="exportXlsx">
          <b-icon-file-earmark-excel-fill class="mr-2"/>
          Exportar para Excel
        </t-button>

        <t-button primary @click="openModal('modal-stock-detail-find-tolerances')">
          <b-icon-person class="mr-2"/>
          Filtrar por Cliente
        </t-button>


      </template>

      <!-- Totalizador em destaque -->
      <b-alert v-if="pageListIsLoaded" show variant="success" class="shadow-lg py-3">
        <div class="d-flex flex-column flex-md-row align-items-md-end justify-content-between">
          <div>
            <div class="text-uppercase small mb-1 font-weight-bold">Peso total (kg)</div>
            <div class="display-4 line-height-1">{{ totalWeight.toLocaleString('pt-BR') }}</div>
          </div>
          <div class="mt-3 mt-md-0 text-md-right">
            <div class="text-uppercase small mb-1 font-weight-bold">Produtos encontrados</div>
            <div class="display-4 line-height-1">
              {{ filteredItems.length }}
            </div>
          </div>
        </div>
      </b-alert>

      <b-container fluid class="py-3">


        <b-table
            v-if="pageListIsLoaded"
            :items="filteredItems"
            :fields="fields"
            thead-class="thead-light"
            table-class="table-fixed-header"
            small
            hover
            striped
            bordered
            head-variant="light"
            responsive="md"
        >
          <!-- Linha de filtros acima do cabeçalho -->
          <template #thead-top>
            <tr>
              <th v-for="f in fields" :key="'filter-'+f.key">
                <!-- TEXT -->
                <b-form-input
                    v-if="!f.filterType || f.filterType === 'text'"
                    v-model="filters[f.key]"
                    @input="onFiltersChanged"
                    size="sm"
                    :placeholder="`Filtrar ${f.label}`"
                />
                <!-- NUMBER -->
                <b-form-input
                    v-else-if="f.filterType === 'number'"
                    v-model="filters[f.key]"
                    @input="onFiltersChanged"
                    size="sm"
                    type="number"
                    :placeholder="`Filtrar ${f.label}`"
                />
                <!-- DATE -->
                <b-form-input
                    v-else-if="f.filterType === 'date'"
                    v-model="filters[f.key]"
                    @input="onFiltersChanged"
                    size="sm"
                    type="date"
                    :placeholder="`Filtrar ${f.label}`"
                />
                <!-- SELECT (para Restrição e Expedição) -->
                <b-form-select
                    v-else-if="f.filterType === 'select'"
                    v-model="filters[f.key]"
                    @change="onFiltersChanged"
                    size="sm"
                    :options="getSelectOptions(f.key)"
                ></b-form-select>
                <!-- RANGE NUMBER (Peso) -->
                <b-input-group
                    v-else-if="f.filterType === 'range-number'"
                    size="sm"
                    class="align-items-center"
                >
                  <b-form-input
                      v-model="filters.totalProducedWeightMin"
                      @input="onFiltersChanged"
                      type="number"
                      placeholder="Mín"
                      min="0"
                      style="max-width:80px"
                  />
                  <b-input-group-text style="padding:0 4px;">–</b-input-group-text>
                  <b-form-input
                      v-model="filters.totalProducedWeightMax"
                      @input="onFiltersChanged"
                      type="number"
                      placeholder="Máx"
                      style="max-width:80px"
                      min="0"
                  />
                </b-input-group>
              </th>
            </tr>
          </template>


          <!-- Formatações de células -->
          <template #cell(totalProducedWeight)="{ item }">
            {{ (item.totalProducedWeight || 0).toLocaleString('pt-BR') }}
          </template>

          <template #cell(productionOrderCodeLabel)="{ item }">
            <!-- mostra a data crua (YYYY-MM-DD) do JSON; ajuste se quiser BR -->
            <a :href="'/custom/production/order/'+item.productionOrderNumber" target="_blank">
              {{ item.productionOrderCodeLabel }}</a>

          </template>

          <template #cell(expeditionNumber)="{ item }">

            <a v-if="item.expeditionNumber != null && item.expeditionNumber !== '' && item.expeditionNumber !== 0"
               :href="'/stock/expedition-order/'+item.expeditionNumber" target="_blank">
              {{ item.expeditionNumber }}</a>

            <span v-else>-</span>

          </template>

          <template #cell(statusProduction)="{ item }">

            <div v-if="item.statusOrder !== 10">{{ item.statusProduction }}</div>
            <span>-</span>

          </template>

          <template #cell(issueDate)="{ item }">
            <!-- mostra a data crua (YYYY-MM-DD) do JSON; ajuste se quiser BR -->
            {{ item.issueDate | date }}
          </template>

          <template #cell(statusOrder)="{ item }">
            <ProductionStatusBadge :status="item.statusOrder"/>
          </template>

          <template #cell(typeRestriction)="{ item }">
            {{ normalizeText(item, 'typeRestriction') }}
          </template>


        </b-table>
      </b-container>

    </t-page-general>

  </t-page>

</template>
<script>
import pageDefaultMixin from "@/mixins/page/pageDefaultMixin"
import pageListMixin from "@/mixins/page/pageListMixin"
import {crudGeneral} from "@/services/resource/GeneralCRUD.service";
import * as XLSX from 'xlsx';
import ProductionStatusBadge from "@/views/modules/production/ProductionOrder/components/ProductionStatusBadge.vue";
import http from "@/services/config/http-common";

export default {
  components: {ProductionStatusBadge},
  mixins: [pageDefaultMixin, pageListMixin],
  name: 'PolimixProductionStockDetail',
  data() {
    return {
      path: "production/order/stock-unique-batch-detail/polimix-liberados/", //Utilizar somente quando fora do padrão
      generalResource: true,
      form: {
        unit: "",
        groupProduct: "",
        model: "",
        productionOrderCodeLabel: "",
        customerId: 0,
        tolerances: "",
      },
      fields: [
        {key: 'groupProduct', label: 'Grupo', filterType: 'select'},
        {key: 'model', label: 'Modelo', filterType: 'select'},
        {key: 'unit', label: 'Unid.', filterType: 'select'},
        {key: 'productionOrderCodeLabel', label: 'OF', filterType: 'text'},
        {key: 'totalProducedWeight', label: 'Peso (kg)', filterType: 'range-number', class: 'text-right'},
        {key: 'issueDate', label: 'Data Produzido', filterType: 'date'},
        {key: 'processFlow', label: 'Fluxo', filterType: 'select'},
        {key: 'statusProduction', label: 'Status Operacional', filterType: 'select'},
        {key: 'typeRestriction', label: 'Restrição', filterType: 'select'},
        {key: 'quantityStock', label: 'Disp. Emb. ', filterType: 'select'},
        {key: 'expeditionNumber', label: 'Expedição', filterType: 'select'},
        {key: 'tolerances', label: 'Tolerâncias', filterType: 'text'},
        {key: 'statusOrder', label: 'Status Geral', filterType: 'select'},
      ],
      //--------------------------------------------------------------------------------------
      //Início de campos personalizados desta tela
      loadCustomerRules: false,
      customerRules: {
        enableAllUnits: false,
        tolerances: []
      },
      customerRulesUnits: "",
      customerRulesTolerances: "",
      items: [],
      filters: {
        groupProduct: '',
        model: '',
        unit: '',
        productionOrderCodeLabel: '',
        totalProducedWeightMin: '',
        totalProducedWeightMax: '',
        issueDate: '',
        processFlow: '',
        typeRestriction: '',
        quantityStock: '',
        expeditionNumber: '',
        statusProduction: '',
        tolerances: '',
        statusOrder: ''
      },
      debounceTimer: null,
      //--------------------------------------------------------------------------------------
    }
  },
  methods: {

    mountedPrepare() {

      this.currentPage.pageTitle = "Extrato de Produtos Liberados"

    },
    async mountedContinued() {

      this.pageListIsLoaded = true;
      this.applyFiltersFromQuery();

    },
    //--------------------------------------------------------------------------------------
    //Início de métodos personalizados desta tela
    /** Dispara quando qualquer filtro muda (com debounce) */
    onFiltersChanged() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.updateQueryString();
      }, 300);
    },

    /** Lê a querystring atual e injeta nos filtros */
    applyFiltersFromQuery() {
      const params = new URLSearchParams(window.location.search);
      Object.keys(this.filters).forEach((k) => {
        if (params.has(k)) {
          // preserva valores em branco se vierem
          this.$set(this.filters, k, params.get(k));
        }
      });
    },

    /** Escreve os filtros atuais na querystring (sem recarregar a página) */
    updateQueryString() {
      const params = new URLSearchParams();
      Object.entries(this.filters).forEach(([k, v]) => {
        if (v !== null && v !== undefined && String(v).trim() !== '') {
          params.set(k, String(v).trim());
        }
      });

      const qs = params.toString();
      const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;

      // Usa pushState para permitir "voltar"; troque por replaceState se quiser sobrescrever
      window.history.pushState({filters: this.filters}, '', newUrl);

    },

    /** Normaliza "typeRestriction" a partir do objeto (cobre vários formatos) */
    normalizeText(item, field) {
      const value = item[field];

      // Se for statusOrder, faz mapeamento manual
      if (field === 'statusOrder') {
        const statusMap = {
          10: 'Liberado',
          4: 'Em andamento'
        };
        return statusMap[value] || (value != null ? `ID ${value}` : '—');
      }

      // Se existir valor string não vazio
      if (value != null && String(value).trim() !== '') {
        return value;
      }

      // Se for numérico e não statusOrder
      if (typeof value === 'number') {
        const map = {
          0: '—' // sem restrição
        };
        return map[value] != null ? map[value] : `ID ${value}`;
      }

      // Caso contrário
      return '—';
    },

    /** Utilitário: normaliza string para busca (lowercase + remove acento) */
    _normalize(s) {
      return s
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
    },

    getSelectOptions(key) {

      const includeNone = ['typeRestriction'].includes(key);
      let values = [];

      if (key === 'typeRestriction') {
        values = this.items.map(i => this.normalizeText(i, 'typeRestriction'));
      } else if (key === 'processFlow') {
        values = this.items.map(i => this.normalizeText(i, 'processFlow'));
      } else if (key === 'statusProduction') {
        values = this.items.map(i => this.normalizeText(i, 'statusProduction'));
      } else if (key === 'statusOrder') {
        values = this.items.map(i => this.normalizeText(i, 'statusOrder'));
      } else if (key === 'expeditionNumber') {
        values = this.items.map(i => i.expeditionNumber);
      } else if (key === 'groupProduct') {
        values = this.items.map(i => i.groupProduct);
      } else if (key === 'model') {
        values = this.items.map(i => i.model);
      } else if (key === 'unit') {
        values = this.items.map(i => i.unit);
      } else if (key === 'quantityStock') {
        values = this.items.map(i => i.quantityStock);
      }

      // normaliza para string e remove vazios/—/nulos
      const cleaned = values
          .map(v => v == null || (v === 0 && key === 'expeditionNumber') ? '' : String(v).trim())
          .filter(v => v !== '' && v !== '—');

      const uniques = Array.from(new Set(cleaned))
          .sort((a, b) => a.localeCompare(b, 'pt-BR'));

      const options = [{value: '', text: 'Todos'}];
      if (includeNone) options.push({value: '__NONE__', text: 'Nenhuma'});
      if (key === 'expeditionNumber') options.push({value: '0', text: 'Nenhuma'});

      return options.concat(uniques.map(v => ({value: v, text: v})));
    },

    async exportXlsx() {
      try {

        if (!XLSX) {
          this.$bvToast && this.$bvToast.toast('Biblioteca XLSX não carregada.', {variant: 'danger'});
          return;
        }

        // 1) Dados já filtrados -> linhas planas na ordem solicitada
        const rows = this.filteredItems.map(it => ({
          groupProduct: it.groupProduct || '',
          model: it.model || '',
          unit: it.unit || '',
          productionOrderCodeLabel: it.productionOrderCodeLabel || '',
          totalProducedWeight: Number(it.totalProducedWeight) || 0,
          issueDate: it.issueDate ? it.issueDate.split('-').reverse().join('/') : '',
          typeRestriction: this.normalizeText(it, 'typeRestriction') || '',
          processFlow: this.normalizeText(it, 'processFlow') || '',
          statusProduction: it.statusOrder === 4 ? (this.normalizeText(it, 'statusProduction') || '') : '-',
          statusOrder: this.normalizeText(it, 'statusOrder') || '',
          quantityStock: (it.quantityStock == null || it.quantityStock === '') ? '' : String(it.quantityStock),
          expeditionNumber: (it.expeditionNumber == null || it.expeditionNumber === '') ? '' : String(it.expeditionNumber),
          tolerances: it.tolerances || '',
        }));

        // 2) Cabeçalho com rótulos amigáveis
        const header = [
          'Grupo', 'Modelo', 'Unid.', 'OF', 'Peso (kg)', 'Data Emissão', 'Restrição', 'Disp. Emb.', 'Expedição', 'Fluxo de Processo', 'Status Operacional', 'Status Geral'
        ];

        // 3) Cria WS só com o cabeçalho em A1
        const ws = XLSX.utils.aoa_to_sheet([header]);

        // 4) Adiciona os dados a partir de A2 (mantendo a ordem das props)
        XLSX.utils.sheet_add_json(ws, rows, {
          origin: 'A2',
          header: [
            'groupProduct', 'model', 'unit', 'productionOrderCodeLabel',
            'totalProducedWeight', 'issueDate', 'typeRestriction', 'quantityStock', 'expeditionNumber', 'processFlow', 'statusProduction', 'statusOrder', 'expeditionNumber', 'tolerances'
          ],
          skipHeader: true
        });

// 5) Linhas para total
        const dataStartRow = 2;                    // primeira linha de dados
        const dataEndRow = rows.length + 1;      // última linha de dados
        const totalRow = dataEndRow + 1;

        // linha "Total"
        XLSX.utils.sheet_add_aoa(ws, [['Total', '', '', '', 0, '', '', '', '']], {origin: `A${totalRow}`});

        // 👉 quantidade de itens na coluna D
        ws[`D${totalRow}`] = {f: `ROWS(D${dataStartRow}:D${dataEndRow})`}; // ou: `COUNTA(D${dataStartRow}:D${dataEndRow})`
        ws[`D${totalRow}`].z = '0'; // formatação inteira

        // soma do peso na coluna E
        ws[`E${totalRow}`] = {f: `SUM(E${dataStartRow}:E${dataEndRow})`};
        ws[`E${totalRow}`].z = '#,##0';

// 6) Larguras e formatação (opcional)
        ws['!cols'] = [
          {wch: 12}, {wch: 14}, {wch: 6}, {wch: 12},
          {wch: 12}, {wch: 12}, {wch: 14}, {wch: 12}, {wch: 12},
        ];
        for (let r = dataStartRow; r <= totalRow; r++) {
          const cell = ws[`E${r}`];
          if (cell) cell.z = '#,##0';
        }

        // 8) Workbook e download
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Estoque');
        const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        XLSX.writeFile(wb, `estoque_filtrado_${stamp}.xlsx`);

      } catch (e) {
        console.error(e);
        this.$bvToast && this.$bvToast.toast('Falha ao gerar XLSX.', {variant: 'danger'});
      }
    },

    onCancelFindTolerances() {
      this.loadCustomerRules = false
      this.form.customerId = 0
    },

    async onChangeCustomerId(val) {

      if (val > 0) {

        this.loadCustomerRules = false

        await http.get("stock/expedition-order/checkCustomerRules/" + val).then((response) => {
          this.customerRules = response.data

          this.customerRulesUnits = this.customerRules.units
              .map(item => item.name)
              .sort((a, b) => a.localeCompare(b, 'pt-BR'))
              .join(', ')

          this.customerRulesTolerances = this.customerRules.tolerances
              .map(item => item.name)
              .sort((a, b) => a.localeCompare(b, 'pt-BR'))
              .join(', ')


          this.loadCustomerRules = true

        })

      }

    },

    filtrarTolerancias() {

      if(!this.customerRules.enableAllUnits){
        this.filters.unit = this.customerRulesUnits
      }

      if(this.customerRules.tolerances.length > 0){
        this.filters.tolerances = this.customerRulesTolerances
      }

      this.$bvModal.hide('modal-stock-detail-find-tolerances')

    },

    //--------------------------------------------------------------------------------------

  },
  async mounted() {

    this.mountedPrepare()

    await crudGeneral.get(this.path)
        .then(response => {

          this.items = response.data
          this.pageListIsLoaded = true
          this.setIsLoadingPage(false)

        })
        .catch(error => {
          console.log('Erro externo: ' + error)
        })

    await this.mountedContinued()

  },

  computed: {
    // lista filtrada conforme os filtros atuais
    filteredItems() {
      const f = this.filters;
      return this.items.filter((row) => {


        // util: checa faixa [min,max]; strings vazias não restringem
        const inRange = (val, minStr, maxStr) => {
          const v = Number(val) || 0;
          const hasMin = String(minStr).trim() !== '';
          const hasMax = String(maxStr).trim() !== '';
          const min = hasMin ? Number(String(minStr).replace(',', '.')) : null;
          const max = hasMax ? Number(String(maxStr).replace(',', '.')) : null;
          const okMin = hasMin ? v >= (isNaN(min) ? -Infinity : min) : true;
          const okMax = hasMax ? v <= (isNaN(max) ? Infinity : max) : true;
          return okMin && okMax;
        };

        // texto => "contém", case-insensitive, ignora acento
        const textMatch = (val, needle) => {
          if (!needle) return true;
          return this._normalize(String(val)).includes(this._normalize(String(needle)));
        };

        // number => aceita número exato OU >= quando for sufixado com "+"
        /*const numberMatch = (val, needle) => {
          // vazio: null/undefined/''
          const isEmpty = needle === null || needle === undefined || String(needle).trim() === '';
          if (isEmpty) return true;

          const s = String(needle).trim();

          // padrão: "N+"
          if (s.endsWith('+')) {
            const base = parseFloat(s.slice(0, -1).replace(',', '.'));
            if (isNaN(base)) return true;
            const v = parseFloat(String(val).replace(',', '.'));
            return !isNaN(v) && v >= base;
          }

          // número exato
          const n = parseFloat(s.replace(',', '.'));
          if (isNaN(n)) return true;
          const v = parseFloat(String(val).replace(',', '.'));
          return !isNaN(v) && v === n;
        };*/


        // date (ISO yyyy-mm-dd) => aceita:
        // - exata: 2024-12-12
        // - intervalo: 2024-12-01..2024-12-31
        const dateMatch = (valIso, needle) => {
          if (!needle) return true;
          if (!valIso) return false;

          const toDate = (s) => new Date(s + 'T00:00:00');
          if (needle.includes('..')) {
            const [start, end] = needle.split('..').map(s => s.trim());
            const d = toDate(valIso);
            const okStart = start ? d >= toDate(start) : true;
            const okEnd = end ? d <= toDate(end) : true;
            return okStart && okEnd;
          }
          return valIso === needle;
        };


        // campos existentes no JSON
        const eqSel = (val, sel) => !sel ? true : this._normalize(String(val)) === this._normalize(String(sel));
        // agora usa igualdade ao invés de "contains"
        const okGroup = eqSel(row.groupProduct, f.groupProduct);
        const okModel = eqSel(row.model, f.model);
        const okUnit = eqSel(row.unit, f.unit);


        const okOF = textMatch(row.productionOrderCodeLabel, f.productionOrderCodeLabel);
        const okTolerance = textMatch(row.tolerances, f.tolerances);

        const okPeso = inRange(
            row.totalProducedWeight || 0,
            f.totalProducedWeightMin,
            f.totalProducedWeightMax
        );
        const okIssue = dateMatch(row.issueDate, f.issueDate);

        const normalizeValue = (v) => {
          const s = v == null ? '' : String(v).trim();
          return (s === '' || s === '—') ? '__NONE__' : s;  // sempre string
        };

        // Restrição
        const restrValue = normalizeValue(this.normalizeText(row, 'typeRestriction'));
        const okRestr = !f.typeRestriction
            ? true
            : f.typeRestriction === '__NONE__'
                ? restrValue === '__NONE__'
                : restrValue === f.typeRestriction;


        //statusProduction
        const statusProductionValue = normalizeValue(this.normalizeText(row, 'statusProduction'));
        const okStepCurrentName = !f.statusProduction
            ? true
            : f.statusProduction === '__NONE__'
                ? statusProductionValue === '__NONE__'
                : statusProductionValue === f.statusProduction;


        //statusProduction
        const statusOrderValue = normalizeValue(this.normalizeText(row, 'statusOrder'));
        const okStatusOrderName = !f.statusOrder
            ? true
            : f.statusOrder === '__NONE__'
                ? statusOrderValue === '__NONE__'
                : statusOrderValue === f.statusOrder;

        // Expedição
        const expValue = normalizeValue(row.expeditionNumber);
        const okExp = !f.expeditionNumber
            ? true
            : f.expeditionNumber === '__NONE__'
                ? expValue === '__NONE__'
                : expValue === f.expeditionNumber;


        // QuantityStock
        const qtyValue = normalizeValue(row.quantityStock);
        const okQuantityStock = !f.quantityStock
            ? true
            : f.quantityStock === '__NONE__'
                ? qtyValue === '__NONE__'
                : qtyValue === f.quantityStock;

        return okGroup && okModel && okUnit && okOF && okPeso && okIssue && okRestr && okTolerance && okQuantityStock && okExp && okStatusOrderName && okStepCurrentName;
      });
    },

    // soma do peso dos itens filtrados
    totalWeight() {
      return this.filteredItems.reduce((sum, r) => sum + (Number(r.totalProducedWeight) || 0), 0);
    },


  }
}
</script>
<style>
/* ambos sticky: filtros (linha 1) e cabeçalho (linha 2) */
.table-fixed-header {
  --sticky-top: 60px;
  --filter-height: 38px;
}

.table-fixed-header thead tr:nth-child(1) th {
  position: sticky;
  top: var(--sticky-top);
  z-index: 6;
  background: #f8f9fa;
}

.table-fixed-header thead tr:nth-child(2) th {
  position: sticky;
  top: calc(var(--sticky-top) + var(--filter-height));
  z-index: 5;
  background: #f8f9fa;
}

</style>